import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import AdCard from '../components/AdCard';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { fetchActiveAds, pickWeightedAd, pickWeightedAds } from '../lib/ads';

function Landing() {
  const { darkMode } = useTheme();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topChargers, setTopChargers] = useState([]);
  const [ads, setAds] = useState([]);

  useEffect(() => {
    fetchActiveAds().then(setAds);
  }, []);

  useEffect(() => {
    // Fetch charging prices
    fetch('/api/charging-prices')
      .then(res => res.text())
      .then(xml => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xml, 'text/xml');
        const items = Array.from(doc.querySelectorAll('item')).map(item => {
          const t = item.querySelector('title')?.textContent || '';
          const desc = item.querySelector('description')?.textContent || '';
          const link = item.querySelector('link')?.textContent || '';
          const m = t.match(/^(\d+)\.\s+(.+?)\s+[–-]\s+([\d.]+)\s+kr\/kWh/);
          if (!m) return null;
          const bestMatch = desc.match(/beste pris:\s*([\d.]+)\s*kr\/kWh/);
          const availMatch = desc.match(/Tilgjengelig:\s*(.+)/);
          return {
            rank: parseInt(m[1]), name: m[2], price: parseFloat(m[3]),
            bestPrice: bestMatch ? parseFloat(bestMatch[1]) : null,
            availability: availMatch ? availMatch[1] : '',
            link
          };
        }).filter(Boolean);
        setTopChargers(items);
      }).catch(() => {});

    api('/api/articles').then(res => res.json()).then(data => {
      setArticles(data.filter(a => a.published).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt) || b.id - a.id));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('nb-NO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const featured = articles[0];
  const rest = articles.slice(1);

  // Ad placement on the Landing grid:
  //   - One "primary" ad high up at article position #2 or #3
  //     (= `rest` index 0 or 1, right after the featured hero).
  //   - Up to two more "secondary" ads further down, at random distinct
  //     positions in articles #4-#10 (= `rest` indices 2..8).
  //   - One "tail" ad rendered as the very last card in the grid, dead
  //     last on the page.
  // Ads are weighted-random picked by priority and never duplicated across
  // the four slots.
  const adPlacement = useMemo(() => {
    const empty = { primaryAd: null, primaryInsertAt: -1, secondaryAds: [], secondaryInserts: [], tailAd: null };
    if (!ads.length || rest.length === 0) return empty;

    // Primary ad (high position).
    const primary = pickWeightedAd(ads);
    if (!primary) return empty;
    const primaryInsertAt = Math.floor(Math.random() * Math.min(rest.length, 2));

    // Secondary ads: pick up to 2 distinct ads from the remaining pool, each
    // at a distinct random index in rest[2..8]. If the article list or the
    // ad pool is too small, we just place fewer.
    const remainingAds = ads.filter(a => a.id !== primary.id);
    const secondaryAds = pickWeightedAds(remainingAds, 2);
    const lowMin = 2;
    const lowMax = Math.min(rest.length - 1, 8);
    const available = [];
    for (let i = lowMin; i <= lowMax; i++) available.push(i);
    // Fisher-Yates-ish shuffle so two inserts never collide.
    for (let i = available.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [available[i], available[j]] = [available[j], available[i]];
    }
    const secondaryInserts = available.slice(0, secondaryAds.length).sort((a, b) => a - b);
    const placedSecondary = secondaryAds.slice(0, secondaryInserts.length);

    // Tail ad: one more ad distinct from primary + secondaries, shown as the
    // very last grid card. Falls back to reusing an existing ad only if the
    // pool is exhausted.
    const usedIds = new Set([primary.id, ...placedSecondary.map(a => a.id)]);
    const tailPool = ads.filter(a => !usedIds.has(a.id));
    const tailAd = tailPool.length > 0 ? pickWeightedAd(tailPool) : null;

    return {
      primaryAd: primary,
      primaryInsertAt,
      secondaryAds: placedSecondary,
      secondaryInserts,
      tailAd
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads, rest.length]);

  // Build an index -> ad[] map so the render loop can decide what to inject
  // at each rest position without nested conditionals.
  const adsByIndex = useMemo(() => {
    const map = new Map();
    if (adPlacement.primaryAd && adPlacement.primaryInsertAt >= 0) {
      map.set(adPlacement.primaryInsertAt, [adPlacement.primaryAd]);
    }
    adPlacement.secondaryAds.forEach((ad, i) => {
      const idx = adPlacement.secondaryInserts[i];
      const list = map.get(idx) || [];
      list.push(ad);
      map.set(idx, list);
    });
    return map;
  }, [adPlacement]);

  return (
    <div className={`min-h-screen pt-20 sm:pt-24 pb-16 transition-colors duration-200 ${darkMode ? 'bg-slate-950' : 'bg-stone-50'}`}>
      <SEO
        path="/"
        description="Effekt tester elbiler grundig i ekte norske forhold. Les artikler om rekkeviddetester, lading og ytelse - sommer og vinter."
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Effekt',
          url: 'https://effektnorge.no',
          description: 'Norges mest grundige elbiltester med fokus på rekkevidde, lading og ytelse.',
          inLanguage: 'nb-NO',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://effektnorge.no/ladestopp?q={search_term_string}',
            'query-input': 'required name=search_term_string'
          }
        }}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6">

        {/* Visually hidden H1 so the home page has one primary heading for
            search engines / screen readers. The rest of the page uses H2/H3. */}
        <h1 className="sr-only">Effekt – grundig elbiltesting i norske forhold</h1>

        {/* Loading skeleton */}
        {loading && (
          <div className="animate-pulse">
            <div className={`${darkMode ? 'bg-slate-800' : 'bg-stone-200'} rounded-2xl h-[400px] mb-8`} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3].map(i => (
                <div key={i} className={`${darkMode ? 'bg-slate-800' : 'bg-stone-200'} rounded-xl h-52 sm:h-72`} />
              ))}
            </div>
          </div>
        )}

        {/* Featured article - hero */}
        {!loading && featured && (
          <Link
            to={`/artikler/${featured.slug}`}
            className="group block mb-10"
          >
            <article className={`relative overflow-hidden rounded-2xl border transition-all duration-150 ${darkMode ? 'bg-slate-900 border-slate-800 hover:border-slate-700' : 'bg-white border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md'}`}>
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {featured.coverImage && (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={featured.coverImage}
                      alt={featured.title}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-200"
                      loading="eager"
                      fetchpriority="high"
                      decoding="async"
                    />
                  </div>
                )}
                <div className={`p-6 sm:p-8 lg:p-10 flex flex-col justify-center ${!featured.coverImage ? 'lg:col-span-2' : ''}`}>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {featured.tags?.slice(0, 2).map(tag => (
                      <Link key={tag} to={`/merke/${encodeURIComponent(tag)}`} className={`px-2.5 py-0.5 text-xs font-medium rounded-full transition-opacity hover:opacity-80 ${darkMode ? 'bg-teal-500 bg-opacity-15 text-teal-400' : 'bg-teal-50 text-teal-700'}`}>
                        {tag}
                      </Link>
                    ))}
                  </div>
                  <h2
                    className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 transition-colors duration-150 leading-tight ${darkMode ? 'text-white group-hover:text-teal-400' : 'text-stone-900 group-hover:text-teal-600'}`}
                    style={{ fontFamily: "'Lora', serif" }}
                  >
                    {featured.title}
                  </h2>
                  <p className={`text-base sm:text-lg leading-relaxed mb-4 line-clamp-3 ${darkMode ? 'text-gray-400' : 'text-stone-600'}`} style={{ fontFamily: "'Lora', serif" }}>
                    {featured.excerpt}
                  </p>
                  <div className={`flex items-center gap-3 text-sm ${darkMode ? 'text-gray-500' : 'text-stone-400'}`}>
                    <span>{featured.author}</span>
                    <span>·</span>
                    <time dateTime={featured.createdAt}>{formatDate(featured.createdAt)}</time>
                  </div>
                </div>
              </div>
            </article>
          </Link>
        )}

        {/* Article grid */}
        {!loading && rest.length > 0 && (
          <section>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Charging widget. Spans the full width on mobile so it
                  doesn't force-stretch the adjacent grid cell (the ~400px
                  widget made ads / articles in the same row look empty
                  and stretched on small screens). */}
              {topChargers.length > 0 && (
                <Link
                  to="/lading"
                  className="group block col-span-2 sm:col-span-1"
                  style={{ animation: 'fadeIn 0.3s ease-out both' }}
                >
                  <div className={`h-full rounded-xl border transition-all duration-150 overflow-hidden ${darkMode ? 'bg-slate-900 border-slate-800 hover:border-slate-700' : 'bg-white border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md'}`}>
                    <div className={`px-4 pt-4 pb-2 flex items-center gap-2 border-b ${darkMode ? 'border-slate-800' : 'border-stone-100'}`}>
                      <div className="w-7 h-7 rounded-lg bg-teal-500 bg-opacity-15 flex items-center justify-center">
                        <svg className="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <span className={`text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>Ladepriser</span>
                    </div>
                    <div className="px-4 py-3">
                      {topChargers.slice(0, 5).map((c, i) => (
                        <div
                          key={c.rank}
                          className={`py-2.5 ${i < 4 ? `border-b ${darkMode ? 'border-slate-800/50' : 'border-stone-100'}` : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className={`font-semibold text-sm ${darkMode ? 'text-gray-100' : 'text-stone-800'}`}>{c.name}</span>
                            <span className={`font-bold text-sm tabular-nums ${darkMode ? 'text-white' : 'text-stone-900'}`}>{c.price.toFixed(2)} <span className={`font-normal text-xs ${darkMode ? 'text-gray-500' : 'text-stone-400'}`}>kr/kWh</span></span>
                          </div>
                          {(c.bestPrice || c.availability) && (
                            <div className="flex items-center justify-between mt-0.5">
                              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-stone-400'}`}>{c.availability || ''}</span>
                              {c.bestPrice && (
                                <span className={`text-xs font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Beste: {c.bestPrice.toFixed(2)} kr</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className={`px-4 py-2.5 border-t flex items-center justify-between ${darkMode ? 'border-slate-800 bg-slate-800/30' : 'border-stone-100 bg-stone-50'}`}>
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-stone-400'}`}>rasklading.no</span>
                      <span className={`text-xs font-medium ${darkMode ? 'text-teal-400 group-hover:text-teal-300' : 'text-teal-600 group-hover:text-teal-500'}`}>Se alle →</span>
                    </div>
                  </div>
                </Link>
              )}

              {rest.map((article, index) => (
                <React.Fragment key={article.id}>
                  {(adsByIndex.get(index) || []).map((ad, k) => (
                    <div
                      key={`ad-${ad.id}-${index}-${k}`}
                      className="h-full"
                      style={{ animation: `fadeIn 0.3s ease-out ${index * 0.05}s both` }}
                    >
                      <AdCard ad={ad} variant="grid" />
                    </div>
                  ))}
                <Link
                  to={`/artikler/${article.slug}`}
                  className="group block"
                  style={{ animation: `fadeIn 0.3s ease-out ${index * 0.05}s both` }}
                >
                  <article className={`h-full rounded-xl overflow-hidden border transition-all duration-150 ${darkMode ? 'bg-slate-900 border-slate-800 hover:border-slate-700' : 'bg-white border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md'}`}>
                    {article.coverImage && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-200"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="p-3 sm:p-5">
                      <div className="hidden sm:flex flex-wrap gap-1.5 mb-2">
                        {article.tags?.slice(0, 2).map(tag => (
                          <Link key={tag} to={`/merke/${encodeURIComponent(tag)}`} className={`px-2 py-0.5 text-xs rounded-full transition-opacity hover:opacity-80 ${darkMode ? 'bg-teal-500 bg-opacity-10 text-teal-400' : 'bg-teal-50 text-teal-700'}`}>
                            {tag}
                          </Link>
                        ))}
                      </div>
                      <h3
                        className={`font-bold text-sm sm:text-base mb-1 sm:mb-2 transition-colors duration-150 line-clamp-2 leading-snug ${darkMode ? 'text-white group-hover:text-teal-400' : 'text-stone-900 group-hover:text-teal-600'}`}
                        style={{ fontFamily: "'Lora', serif" }}
                      >
                        {article.title}
                      </h3>
                      <p className={`text-xs sm:text-sm leading-relaxed line-clamp-2 mb-2 sm:mb-3 hidden sm:block ${darkMode ? 'text-gray-500' : 'text-stone-500'}`} style={{ fontFamily: "'Lora', serif" }}>
                        {article.excerpt}
                      </p>
                      <div className={`text-xs ${darkMode ? 'text-gray-600' : 'text-stone-400'}`}>
                        <time dateTime={article.createdAt}>{formatDate(article.createdAt)}</time>
                      </div>
                    </div>
                  </article>
                </Link>
                </React.Fragment>
              ))}

              {/* Tail ad — dead last in the grid, after every article card. */}
              {adPlacement.tailAd && (
                <div
                  className="h-full"
                  style={{ animation: `fadeIn 0.3s ease-out ${rest.length * 0.05}s both` }}
                >
                  <AdCard ad={adPlacement.tailAd} variant="grid" />
                </div>
              )}
            </div>
          </section>
        )}

        {/* See all link */}
        {!loading && articles.length > 0 && (
          <div className="text-center mt-10">
            <Link
              to="/artikler"
              className={`inline-flex items-center gap-2 px-6 py-3 font-medium rounded-xl transition-colors duration-150 text-sm border ${darkMode ? 'bg-slate-800 hover:bg-slate-700 text-gray-300 hover:text-white border-slate-700' : 'bg-white hover:bg-stone-50 text-stone-600 hover:text-stone-900 border-stone-200'}`}
            >
              Alle artikler
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        )}

        {/* Empty state */}
        {!loading && articles.length === 0 && (
          <div className="text-center py-20">
            <h1 className="text-4xl sm:text-5xl font-bold text-teal-500 mb-4">Effekt</h1>
            <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-stone-500'}`}>Artikler kommer snart.</p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default Landing;
